/* istanbul ignore file: even though all branches are tested and verified to be running in test runner, branch coverage is not met. weird bug. */
import { either } from 'fp-ts';
import { pred_isUiColorItem, pred_isUiLabelItem, pred_isUiRegionItem } from 'utils/cfUiItems/guards';
import { ReactLeft, UI_STATES } from 'utils/uiStates/uiStates';
import { PredUiColorItem, PredUiImageItem, PredUiItem, PredUiLabelItem, PredUiRegionItem, PredUiTextItem } from 'utils/cfUiItems/predictions';
import { getShapeProps } from 'utils/predictions/predictions';
import { isEmpty } from 'rambda';

const leftUnsupported = either.left({
  type: UI_STATES.invalid,
  props: { reason: 'Model output cannot be rendered, you can view the raw output JSON instead.' },
} as ReactLeft);

const leftEmpty = either.left({
  type: UI_STATES.empty,
  props: { reason: 'No detections from model.', title: 'Failure' },
} as ReactLeft);

// guarantees we never send an unsupported or empty list to component
export function viewModelOutputList(outputData: CF.API.ModelOutputs.ModelOutput['data']): either.Either<ReactLeft, { items: PredUiItem[] }> {
  if (isEmpty(outputData)) {
    return leftEmpty;
  }
  if ('regions' in outputData) return prepareRegionListE(outputData.regions);
  if ('concepts' in outputData) return prepareConceptListE(outputData.concepts);
  if ('colors' in outputData) return prepareColorListE(outputData.colors);
  if ('text' in outputData) {
    return either.right({
      items: [
        {
          key: 'region.id',
          text: outputData.text,
          prediction: {
            score: null,
          },
        } as PredUiTextItem,
      ],
    });
  }
  if ('image' in outputData) {
    return either.right({
      items: [
        {
          key: 'image.id',
          image: outputData.image,
          prediction: {
            score: null,
          },
        } as PredUiImageItem,
      ],
    });
  }

  return leftUnsupported;
}

/* istanbul ignore next: unnecessary branch coverage */
function escapeEmpty(something?: unknown[] | null): either.Either<ReactLeft, never> | false {
  return something && something.length ? false : either.left({ type: UI_STATES.empty, props: { reason: 'No output from model.' } });
}

function prepareRegionListE(regionList?: CF.API.RegionsEntity[] | null): either.Either<ReactLeft, { items: PredUiRegionItem[] }> {
  const possiblyEmptyUiLeft = escapeEmpty(regionList);
  if (possiblyEmptyUiLeft) return possiblyEmptyUiLeft;
  return either.right({
    items: (regionList as CF.API.RegionsEntity[])
      // transform concept to UiLabelItem
      .map((region) =>
        region.data.concepts?.length
          ? ({
              key: region.id,
              concepts: region.data.concepts,
              regionData: getShapeProps(region),
              prediction: {
                score: region.value || region.data.concepts?.[0].value || null,
              },
            } as PredUiRegionItem)
          : {
              key: region.id,
              regionData: getShapeProps(region),
              prediction: {
                score: region.value || null,
              },
              text: region.data.text,
            },
      )
      .filter<PredUiRegionItem>(pred_isUiRegionItem),
  });
}

function prepareConceptListE(conceptList?: CF.API.Concept[] | null): either.Either<ReactLeft, { items: PredUiLabelItem[] }> {
  const possiblyEmptyUiLeft = escapeEmpty(conceptList);
  if (possiblyEmptyUiLeft) return possiblyEmptyUiLeft;
  return either.right({
    items: (conceptList as CF.API.Concept[])
      // transform concept to UiLabelItem
      .map((concept) => ({
        key: concept.id,
        concept,
        prediction: {
          score: concept.value || null,
        },
      }))
      .filter<PredUiLabelItem>(pred_isUiLabelItem),
  });
}

function prepareColorListE(conceptList?: CF.API.ColorsEntity[] | null): either.Either<ReactLeft, { items: PredUiColorItem[] }> {
  const possiblyEmptyUiLeft = escapeEmpty(conceptList);
  if (possiblyEmptyUiLeft) return possiblyEmptyUiLeft;
  return either.right({
    items: (conceptList as CF.API.ColorsEntity[])
      // transform to UiColorItem
      .map((color) => ({
        key: color.raw_hex,
        color,
        prediction: {
          score: color.value || null,
        },
      }))
      .filter<PredUiColorItem>(pred_isUiColorItem),
  });
}
